/**
 * CTRL_05 Configuration Panel - Training Data Audit Trail
 */
import React, { useState, useEffect } from 'react';
import { Card, Switch, Collapse, Button, Alert, Spin, Select, Slider, Form, Checkbox } from 'antd';
import { SaveOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { getControlConfig, saveControlConfig } from '../api/trustgridApi';
import { showError, showSuccess } from '../../../utils/notifications';

const { Panel } = Collapse;
const { Option } = Select;

const CONSENT_TYPES = [
  { value: 'explicit', label: 'Explicit Consent', description: 'Active opt-in' },
  { value: 'implicit', label: 'Implicit Consent', description: 'Assumed through action' },
  { value: 'legitimate_interest', label: 'Legitimate Interest', description: 'GDPR Art 6(1)(f)' },
  { value: 'not_required', label: 'Not Required', description: 'Non-personal data' }
];

function CTRL_05_ConfigPanel({ project, projectId, controlCode = 'CTRL_05' }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [configData, setConfigData] = useState({
    enabled: false,
    require_dataset_manifest: true,
    require_consent_tracking: true,
    consent_types: ['explicit', 'legitimate_interest'],
    minimum_data_quality_score: 75,
    require_ropa_documentation: false,
    require_data_source_approval: true
  });

  useEffect(() => {
    if (projectId) {
      fetchConfig();
    }
  }, [projectId]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await getControlConfig(projectId, controlCode);
      if (res.data) {
        setConfig(res.data);
        setConfigData({
          enabled: res.data.enabled || false,
          ...res.data.config
        });
      }
    } catch (error) {
      console.error('Failed to fetch config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        project_id: projectId,
        control_code: controlCode,
        enabled: configData.enabled,
        config: {
          require_dataset_manifest: configData.require_dataset_manifest,
          require_consent_tracking: configData.require_consent_tracking,
          consent_types: configData.consent_types,
          minimum_data_quality_score: configData.minimum_data_quality_score,
          require_ropa_documentation: configData.require_ropa_documentation,
          require_data_source_approval: configData.require_data_source_approval
        }
      };

      await saveControlConfig(projectId, controlCode, payload);
      showSuccess('Configuration saved successfully');
      fetchConfig();
    } catch (error) {
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field, value) => {
    setConfigData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px' }}><Spin size="large" /></div>;
  }

  return (
    <Card>
      <div style={{ marginBottom: '20px' }}>
        <h3>CTRL_05: Training Data Audit Trail Configuration</h3>
        <p style={{ color: '#666' }}>
          Document sources, consent, and quality of training data for audit and compliance
        </p>
      </div>

      <Collapse defaultActiveKey={configData.enabled ? ['1', '2'] : ['1']}>
        <Panel header="1. Enable Training Data Audit Control" key="1">
          <Switch
            checked={configData.enabled}
            onChange={(checked) => updateConfig('enabled', checked)}
            checkedChildren="Enabled"
            unCheckedChildren="Disabled"
          />
          <p style={{ marginTop: '12px', color: '#666' }}>
            When enabled, training events will be checked for data audit trail metadata
          </p>
          <Alert
            message="About Data Audit Trails"
            description="EU AI Act Art 10(4) requires documentation of training data sources, consent, and quality. GDPR Art 30 requires ROPA for personal data processing."
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            style={{ marginTop: '12px' }}
          />
        </Panel>

        {configData.enabled && (
          <Panel header="2. Audit Trail Configuration" key="2">
            <Form layout="vertical">
              <Form.Item label="Dataset Requirements">
                <div style={{ marginBottom: '12px' }}>
                  <Switch
                    checked={configData.require_dataset_manifest}
                    onChange={(checked) => updateConfig('require_dataset_manifest', checked)}
                    checkedChildren="Required"
                    unCheckedChildren="Optional"
                    style={{ marginRight: '12px' }}
                  />
                  <span>Require dataset manifest (dataset_id)</span>
                </div>
                <div>
                  <Switch
                    checked={configData.require_data_source_approval}
                    onChange={(checked) => updateConfig('require_data_source_approval', checked)}
                    checkedChildren="Required"
                    unCheckedChildren="Optional"
                    style={{ marginRight: '12px' }}
                  />
                  <span>Require data source approval documentation</span>
                </div>
              </Form.Item>

              <Form.Item label="Consent Tracking">
                <div style={{ marginBottom: '12px' }}>
                  <Switch
                    checked={configData.require_consent_tracking}
                    onChange={(checked) => updateConfig('require_consent_tracking', checked)}
                    checkedChildren="Required"
                    unCheckedChildren="Optional"
                    style={{ marginRight: '12px' }}
                  />
                  <span>Require consent status tracking</span>
                </div>

                {configData.require_consent_tracking && (
                  <div style={{ marginTop: '12px' }}>
                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Acceptable Consent Types:</label>
                    <Checkbox.Group
                      value={configData.consent_types || []}
                      onChange={(value) => updateConfig('consent_types', value)}
                    >
                      {CONSENT_TYPES.map(type => (
                        <div key={type.value} style={{ marginBottom: '8px' }}>
                          <Checkbox value={type.value}>
                            <strong>{type.label}</strong> - {type.description}
                          </Checkbox>
                        </div>
                      ))}
                    </Checkbox.Group>
                  </div>
                )}
              </Form.Item>

              <Form.Item label={`Minimum Data Quality Score: ${configData.minimum_data_quality_score}`}>
                <Slider
                  min={0}
                  max={100}
                  value={configData.minimum_data_quality_score}
                  onChange={(value) => updateConfig('minimum_data_quality_score', value)}
                  marks={{ 0: '0', 50: '50', 75: '75', 100: '100' }}
                />
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  Minimum acceptable quality score for training datasets
                </p>
              </Form.Item>

              <Form.Item label="ROPA Compliance">
                <Switch
                  checked={configData.require_ropa_documentation}
                  onChange={(checked) => updateConfig('require_ropa_documentation', checked)}
                  checkedChildren="Required"
                  unCheckedChildren="Optional"
                />
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  Require Record of Processing Activities (ROPA) documentation for training data
                </p>
              </Form.Item>
            </Form>
          </Panel>
        )}
      </Collapse>

      {configData.enabled && (
        <Alert
          message="Configuration Summary"
          description={
            <div>
              <div><strong>Dataset Manifest:</strong> {configData.require_dataset_manifest ? 'Required' : 'Optional'}</div>
              <div><strong>Consent Tracking:</strong> {configData.require_consent_tracking ? 'Required' : 'Optional'}</div>
              {configData.require_consent_tracking && (
                <div><strong>Consent Types:</strong> {configData.consent_types?.join(', ') || 'None'}</div>
              )}
              <div><strong>Min Quality Score:</strong> {configData.minimum_data_quality_score}</div>
              <div><strong>ROPA Documentation:</strong> {configData.require_ropa_documentation ? 'Required' : 'Optional'}</div>
            </div>
          }
          type="info"
          showIcon
          style={{ marginTop: '16px' }}
        />
      )}

      <Button
        type="primary"
        icon={<SaveOutlined />}
        onClick={handleSave}
        loading={saving}
        style={{ marginTop: '16px' }}
        size="large"
      >
        Save Configuration
      </Button>
    </Card>
  );
}

export default CTRL_05_ConfigPanel;
